import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type Row,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

/*
 * Canonical shadcn + @tanstack/react-table wrapper. Replaces mui-datatables
 * for new pages. Intentionally simple: sorting, filtering, pagination, and
 * column visibility are wired in but the consumer drives the toolbar
 * (search input, column toggles, etc.) so each page can match its own UX.
 *
 * Pagination has two modes:
 *  - client-side (default): the table holds the whole data array and
 *    paginates locally.
 *  - server-side: pass `manualPagination` with the controlled state +
 *    onPageChange callback. The table renders only whatever `data` you
 *    hand it; previous/next dispatch your callback so the consumer can
 *    re-fetch a new page from the API.
 *
 * For complex tables (row selection, expand, virtualised rows) compose
 * the underlying useReactTable() directly; this wrapper is just the
 * common case.
 */

interface ManualPagination {
  pageIndex: number;
  pageCount: number;
  onPageChange: (next: number) => void;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /** Renders above the table — typical use is a search input + column menu. */
  toolbar?: (table: ReturnType<typeof useReactTable<TData>>) => React.ReactNode;
  /** Text shown when the data array is empty. */
  emptyMessage?: React.ReactNode;
  /** Set to false to hide the pagination footer. */
  pagination?: boolean;
  /** Default page size when client-side pagination is on. */
  pageSize?: number;
  /** Controlled server-side pagination. When set, `pageSize` is ignored. */
  manualPagination?: ManualPagination;
  /** Per-row class hook — e.g. highlight a "root" or "archived" row. */
  getRowClassName?: (row: Row<TData>) => string | undefined;
  /** Optional click handler on the row body (excludes interactive cells). */
  onRowClick?: (row: TData) => void;
  /** When true, body renders skeleton rows instead of real data. */
  loading?: boolean;
  /** Number of skeleton rows to render while loading. */
  skeletonRows?: number;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  toolbar,
  emptyMessage = "No results.",
  pagination = true,
  pageSize = 10,
  manualPagination,
  getRowClassName,
  onRowClick,
  loading = false,
  skeletonRows = 5,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const isServerPagination = pagination && !!manualPagination;
  const isClientPagination = pagination && !manualPagination;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: isClientPagination ? getPaginationRowModel() : undefined,
    manualPagination: isServerPagination,
    pageCount: isServerPagination ? manualPagination.pageCount : undefined,
    initialState: isClientPagination ? { pagination: { pageSize } } : undefined,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      ...(isServerPagination
        ? {
            pagination: {
              pageIndex: manualPagination.pageIndex,
              pageSize: 1, // ignored when manualPagination is true; needs a value
            },
          }
        : {}),
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
  });

  const pageIndex = isServerPagination
    ? manualPagination.pageIndex
    : table.getState().pagination.pageIndex;
  const pageCount = isServerPagination
    ? manualPagination.pageCount
    : table.getPageCount();
  const canPrev = isServerPagination ? pageIndex > 0 : table.getCanPreviousPage();
  const canNext = isServerPagination
    ? pageIndex < pageCount - 1
    : table.getCanNextPage();
  const goPrev = () =>
    isServerPagination
      ? manualPagination.onPageChange(pageIndex - 1)
      : table.previousPage();
  const goNext = () =>
    isServerPagination
      ? manualPagination.onPageChange(pageIndex + 1)
      : table.nextPage();

  return (
    <div className="space-y-3">
      {toolbar ? toolbar(table) : null}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {group.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: skeletonRows }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`}>
                  {columns.map((_col, cellIndex) => (
                    <TableCell key={`skeleton-${rowIndex}-${cellIndex}`}>
                      <Skeleton className="h-4 w-full max-w-[200px]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={
                    onRowClick ? () => onRowClick(row.original) : undefined
                  }
                  className={cn(
                    onRowClick && "cursor-pointer",
                    getRowClassName?.(row),
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {pagination ? (
        <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
          <span>
            {t("common.data-table.page-of", "Page {{current}} of {{total}}", {
              current: pageIndex + 1,
              total: pageCount || 1,
            })}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={!canPrev || loading}
            >
              {t("common.back", "Previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goNext}
              disabled={!canNext || loading}
            >
              {t("common.next", "Next")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
